"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  fitsCell,
  HeatmapChart,
  heatmapSizes,
} from "@/components/tools/color-charts/heatmap-svg";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { type NumberFormat, valueText } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type LabeledTable,
  parseLabeledRows,
  seriesNames,
} from "@/lib/tool-kit/charts/parse";
import {
  type ColorBy,
  cellFractions,
  gridValues,
  heatmapScale,
  paletteById,
} from "@/lib/tool-kit/color-charts/heatmap";
import {
  HEATMAP_PALETTES,
  type HeatmapPaletteId,
} from "@/lib/tool-kit/color-charts/heatmap-palettes";

/** English strings. A translated content file overrides these through `ui`. */
export const HEATMAP_MAKER_STRINGS = {
  dataLabel:
    "Your data — a table: row names down the side, a number in each cell",
  dataHint:
    "One row per line: a name, then one number per column. A first line without numbers names the columns. Separate cells with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. Leave a cell empty for a missing value.",
  exampleData:
    "Course, Mon, Tue, Wed, Thu, Fri\nStatistics, 2.5, 3, 2, 3.5, 1.5\nChemistry, 3, 2.5, 3.5, 2, 1\nHistory, 1.5, 2, 2.5, 3, 2\nLiterature, 2, 1.5, 1, 2.5, 3.5",
  firstLineHeader: "The first line names the columns",
  exampleTitle: "Mean Daily Study Hours by Course and Weekday",
  exampleColumnTitle: "Weekday",
  exampleRowTitle: "Course",
  rowsDetected: "one:{count} row|other:{count} rows",
  columnsDetected: "one:{count} column|other:{count} columns",
  columnDefault: "Column {number}",
  paletteLabel: "Color palette",
  paletteViridis: "Viridis — sequential",
  paletteCividis: "Cividis — sequential, for color-vision deficiency",
  paletteBlues: "Blues — sequential, ColorBrewer",
  paletteRdbu: "Red–Blue — diverging, ColorBrewer",
  palettePuor: "Orange–Purple — diverging, ColorBrewer",
  creditViridis:
    "Viridis: Nathaniel Smith, Stéfan van der Walt and Eric Firing, released under CC0.",
  creditCividis:
    "Cividis: Nuñez, Anderton and Renslow (2018), PLOS ONE 13(7): e0199239; copyright 2017 Battelle Memorial Institute, BSD-style licence.",
  creditBlues:
    "ColorBrewer: Cynthia Brewer, Mark Harrower and The Pennsylvania State University, Apache License 2.0.",
  reverseLabel: "Reverse the palette",
  colorByLabel: "Color the cells by",
  colorByTable: "One scale for the whole table",
  colorByRow: "Each row against its own range",
  colorByColumn: "Each column against its own range",
  colorByHint:
    "One scale means the same color always stands for the same number. Scaling each row or column separately shows rank within it instead — use that when the rows or columns are in different units.",
  scaleMin: "Scale minimum",
  scaleMid: "Midpoint (diverging palettes)",
  scaleMax: "Scale maximum",
  scaleAuto: "Auto",
  scaleHint:
    "Auto uses the lowest and highest value. A diverging palette is centered on zero when the data cross zero, and on the median otherwise.",
  scaleBad:
    "The minimum must be below the midpoint, which must be below the maximum, so the automatic scale is used.",
  divergingByGroup:
    "A diverging palette needs a meaningful midpoint, and a row or column has none, so Viridis is used for this view.",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "Column axis title",
  yAxisTitle: "Row axis title",
  axisTitlePlaceholderX: "Columns",
  axisTitlePlaceholderY: "Rows",
  valueLabels: "Print the value in each cell",
  valueLabelsHidden:
    "The values are hidden because the cells are too narrow to hold them. Use fewer columns, or shorter numbers.",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  rowColumn: "Row",
  legendHighRow: "Highest in row",
  legendLowRow: "Lowest in row",
  legendHighColumn: "Highest in column",
  legendLowColumn: "Lowest in column",
  chartLabel: "Heatmap",
  chartDescription:
    "Heatmap of {rows} by {columns}. Highest value: {max}, {maxRow}, {maxColumn}. Lowest value: {min}, {minRow}, {minColumn}.",
  enterData:
    "Enter at least one row with a name and a number to draw the heatmap.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  invalidCells:
    "one:{count} cell is not a number and is drawn as missing.|other:{count} cells are not numbers and are drawn as missing.",
  tooManyRows: "Only the first {max} rows are drawn.",
  tooManyColumns: "Only the first {max} columns are drawn.",
} as const;

type Strings = typeof HEATMAP_MAKER_STRINGS;

const MISSING = "—";
const MAX_ROWS = 100;
const MAX_COLUMNS = 40;

const PALETTE_NAME_KEY = {
  viridis: "paletteViridis",
  cividis: "paletteCividis",
  blues: "paletteBlues",
  rdbu: "paletteRdbu",
  puor: "palettePuor",
} as const satisfies Record<HeatmapPaletteId, keyof Strings>;

const PALETTE_CREDIT_KEY = {
  viridis: "creditViridis",
  cividis: "creditCividis",
  blues: "creditBlues",
  rdbu: "creditBlues",
  puor: "creditBlues",
} as const satisfies Record<HeatmapPaletteId, keyof Strings>;

interface Extreme {
  value: number;
  row: string;
  column: string;
}

function extremes(
  grid: (number | null)[][],
  rows: string[],
  columns: string[]
): { max: Extreme | null; min: Extreme | null } {
  let max: Extreme | null = null;
  let min: Extreme | null = null;
  for (const [r, cells] of grid.entries()) {
    for (const [c, value] of cells.entries()) {
      if (value === null) {
        continue;
      }
      const point = { value, row: rows[r] ?? "", column: columns[c] ?? "" };
      if (!max || value > max.value) {
        max = point;
      }
      if (!min || value < min.value) {
        min = point;
      }
    }
  }
  return { max, min };
}

function summarize(
  grid: (number | null)[][],
  rows: string[],
  columns: string[],
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string {
  const { max, min } = extremes(grid, rows, columns);
  return t(strings.chartDescription, {
    rows: tPlural(strings.rowsDetected, locale, rows.length, {
      count: fmt(rows.length),
    }),
    columns: tPlural(strings.columnsDetected, locale, columns.length, {
      count: fmt(columns.length),
    }),
    max: max ? valueText(fmt, max.value) : "",
    maxRow: max?.row ?? "",
    maxColumn: max?.column ?? "",
    min: min ? valueText(fmt, min.value) : "",
    minRow: min?.row ?? "",
    minColumn: min?.column ?? "",
  });
}

function tableNotices(
  table: LabeledTable,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const notes: string[] = [];
  if (table.rows.length === 0) {
    notes.push(strings.enterData);
  }
  if (table.skipped.length > 0) {
    notes.push(
      tPlural(strings.skippedLines, locale, table.skipped.length, {
        lines: table.skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (table.invalidCells > 0) {
    notes.push(
      tPlural(strings.invalidCells, locale, table.invalidCells, {
        count: fmt(table.invalidCells),
      })
    );
  }
  if (table.rows.length > MAX_ROWS) {
    notes.push(t(strings.tooManyRows, { max: fmt(MAX_ROWS) }));
  }
  if (table.seriesCount > MAX_COLUMNS) {
    notes.push(t(strings.tooManyColumns, { max: fmt(MAX_COLUMNS) }));
  }
  return notes;
}

function ScaleFields({
  strings,
  diverging,
  low,
  mid,
  high,
  setLow,
  setMid,
  setHigh,
}: {
  strings: Strings;
  diverging: boolean;
  low: string;
  mid: string;
  high: string;
  setLow: (value: string) => void;
  setMid: (value: string) => void;
  setHigh: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div
        className={
          diverging ? "grid gap-4 sm:grid-cols-3" : "grid gap-4 sm:grid-cols-2"
        }
      >
        <TextField
          id="hmm-min"
          label={strings.scaleMin}
          numeric
          onChange={setLow}
          placeholder={strings.scaleAuto}
          value={low}
        />
        {diverging ? (
          <TextField
            id="hmm-mid"
            label={strings.scaleMid}
            numeric
            onChange={setMid}
            placeholder={strings.scaleAuto}
            value={mid}
          />
        ) : null}
        <TextField
          id="hmm-max"
          label={strings.scaleMax}
          numeric
          onChange={setHigh}
          placeholder={strings.scaleAuto}
          value={high}
        />
      </div>
      <p className={HINT}>{strings.scaleHint}</p>
    </div>
  );
}

export default function HeatmapMakerWidget() {
  const strings = useToolUi(HEATMAP_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [paletteId, setPaletteId] = useState<HeatmapPaletteId>("viridis");
  const [reverse, setReverse] = useState(false);
  const [by, setBy] = useState<ColorBy>("table");
  const [low, setLow] = useState("");
  const [mid, setMid] = useState("");
  const [high, setHigh] = useState("");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleColumnTitle,
    yTitle: strings.exampleRowTitle,
    color: "blue",
    valueLabels: true,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseLabeledRows(raw, { header: headerChoice ?? undefined });
  const columnCount = Math.min(table.seriesCount, MAX_COLUMNS);
  const rows = table.rows.slice(0, MAX_ROWS);
  const rowLabels = rows.map((row) => row.label);
  const columnLabels = seriesNames(table, (index) =>
    t(strings.columnDefault, { number: fmt(index + 1) })
  ).slice(0, columnCount);
  const grid = rows.map((row) => row.values.slice(0, columnCount));

  const grouped = by !== "table";
  const requested = paletteById(paletteId);
  const palette =
    grouped && requested.kind === "diverging"
      ? paletteById("viridis")
      : requested;
  const diverging = palette.kind === "diverging";
  const values = gridValues(grid);
  const scale = heatmapScale(values, diverging, {
    low: parseNumberInput(low),
    mid: parseNumberInput(mid),
    high: parseNumberInput(high),
  });
  const fractions = cellFractions(grid, scale, by);
  const highText =
    by === "row" ? strings.legendHighRow : strings.legendHighColumn;
  const lowText = by === "row" ? strings.legendLowRow : strings.legendLowColumn;
  const sizes = heatmapSizes({
    rowLabels,
    columns: columnCount,
    scale,
    by,
    fmt,
    highText,
    lowText,
    hasYTitle: style.yTitle.trim() !== "",
  });
  const widest = values.reduce((longest, v) => {
    const text = valueText(fmt, v);
    return text.length > longest.length ? text : longest;
  }, "");
  const labelsFit = fitsCell(widest, sizes.cellWidth);
  const drawable = rows.length > 0 && columnCount > 0;

  const notices = tableNotices(table, strings, locale, fmt);
  if (grouped && requested.kind === "diverging") {
    notices.push(strings.divergingByGroup);
  }
  if (!grouped && scale.bad) {
    notices.push(strings.scaleBad);
  }
  if (drawable && style.valueLabels && !labelsFit) {
    notices.push(strings.valueLabelsHidden);
  }
  const head = [
    table.header?.[0]?.trim() || strings.rowColumn,
    ...columnLabels,
  ];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="hmm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={7}
        value={raw}
      />
      <Checkbox
        checked={headerChoice ?? table.autoHeader}
        label={strings.firstLineHeader}
        onChange={setHeaderChoice}
      />
      <div aria-live="polite" className="space-y-2">
        {rows.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.rowsDetected, locale, rows.length, {
              count: fmt(rows.length),
            })}
            {" · "}
            {tPlural(strings.columnsDetected, locale, columnCount, {
              count: fmt(columnCount),
            })}
          </p>
        ) : null}
        {notices.map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="space-y-3">
        <SelectField
          id="hmm-palette"
          label={strings.paletteLabel}
          onChange={setPaletteId}
          options={HEATMAP_PALETTES.map((p) => ({
            value: p.id,
            label: strings[PALETTE_NAME_KEY[p.id]],
          }))}
          value={paletteId}
        />
        <p className={HINT}>{strings[PALETTE_CREDIT_KEY[paletteId]]}</p>
        <Checkbox
          checked={reverse}
          label={strings.reverseLabel}
          onChange={setReverse}
        />
      </div>
      <div className="space-y-3">
        <PillGroup
          label={strings.colorByLabel}
          name="hmm-by"
          onChange={setBy}
          options={[
            { value: "table", label: strings.colorByTable },
            { value: "row", label: strings.colorByRow },
            { value: "column", label: strings.colorByColumn },
          ]}
          value={by}
        />
        <p className={HINT}>{strings.colorByHint}</p>
        {grouped ? null : (
          <ScaleFields
            diverging={diverging}
            high={high}
            low={low}
            mid={mid}
            setHigh={setHigh}
            setLow={setLow}
            setMid={setMid}
            strings={strings}
          />
        )}
      </div>
      {drawable ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([
            head,
            ...grid.map((cells, r) => [rowLabels[r] ?? "", ...cells]),
          ])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "heatmap")}
          labels={strings}
          note={style.note}
          table={{
            head,
            rows: grid.map((cells, r) => [
              rowLabels[r] ?? "",
              ...cells.map((v) => (v === null ? MISSING : valueText(fmt, v))),
            ]),
          }}
          title={style.title}
        >
          <HeatmapChart
            by={by}
            columnLabels={columnLabels}
            description={summarize(
              grid,
              rowLabels,
              columnLabels,
              strings,
              locale,
              fmt
            )}
            fmt={fmt}
            fractions={fractions}
            grid={grid}
            highText={highText}
            id="hmm-chart"
            label={style.title.trim() || strings.chartLabel}
            lowText={lowText}
            palette={palette}
            reverse={reverse}
            rowLabels={rowLabels}
            scale={scale}
            showValues={style.valueLabels && labelsFit}
            style={style}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        axisLabels={{
          xAxisTitle: strings.xAxisTitle,
          yAxisTitle: strings.yAxisTitle,
          axisTitlePlaceholderX: strings.axisTitlePlaceholderX,
          axisTitlePlaceholderY: strings.axisTitlePlaceholderY,
        }}
        idPrefix="hmm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
